import type { EnrolledSession } from "../models/types";
import { fetchEncryptedContent } from "../api/client";

const SIGNING_PREFIX_V2 = "sundius-lb-v2:";

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

async function signingKeyFromSecretB64(secretB64: string): Promise<CryptoKey> {
  const secretData = base64ToBytes(secretB64) as BufferSource;
  const secretDigest = await crypto.subtle.digest("SHA-256", secretData);
  const prefix = new TextEncoder().encode(SIGNING_PREFIX_V2);
  const combined = new Uint8Array(prefix.length + secretDigest.byteLength);
  combined.set(prefix, 0);
  combined.set(new Uint8Array(secretDigest), prefix.length);
  const keyDigest = await crypto.subtle.digest("SHA-256", combined);
  return crypto.subtle.importKey(
    "raw",
    keyDigest,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
}

function bytesToHex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function hmacHex(key: CryptoKey, canonical: string): Promise<string> {
  const sig = await crypto.subtle.sign(
    "HMAC",
    key,
    new TextEncoder().encode(canonical),
  );
  return bytesToHex(sig);
}

export async function signGet(
  secretB64: string,
  path: string,
  mode: string,
  timestamp: number,
): Promise<string> {
  const key = await signingKeyFromSecretB64(secretB64);
  const canonical = `${timestamp}|GET|${path}|${mode}`;
  return hmacHex(key, canonical);
}

export async function signPostScore(
  secretB64: string,
  body: {
    entry_id: string;
    person_id: string;
    device_id: string;
    mode: string;
    score: number;
    total: number;
    duration_ms: number;
    completed_at: number;
  },
  timestamp: number,
): Promise<string> {
  const key = await signingKeyFromSecretB64(secretB64);
  const payload = [
    body.entry_id,
    body.person_id,
    body.device_id,
    body.mode,
    String(body.score),
    String(body.total),
    String(body.duration_ms),
    String(body.completed_at),
  ].join("|");
  const canonical = `${timestamp}|POST|/scores|${payload}`;
  return hmacHex(key, canonical);
}

export async function signGeneric(
  secretB64: string,
  method: string,
  path: string,
  bodyCanonical: string,
  timestamp: number,
): Promise<string> {
  const key = await signingKeyFromSecretB64(secretB64);
  const canonical = `${timestamp}|${method}|${path}|${bodyCanonical}`;
  return hmacHex(key, canonical);
}

export async function decryptAesGcm(
  combined: ArrayBuffer,
  bundleKeyB64: string,
): Promise<ArrayBuffer> {
  const keyBytes = base64ToBytes(bundleKeyB64) as BufferSource;
  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );
  const data = new Uint8Array(combined);
  const iv = data.slice(0, 12);
  const ciphertext = data.slice(12);
  return crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
}

export async function decryptBundleJson<T>(
  session: EnrolledSession,
  bundleKeyB64: string,
): Promise<T> {
  const combined = await fetchEncryptedContent(session, "sundius_family.enc");
  const plain = await decryptAesGcm(combined, bundleKeyB64);
  const text = new TextDecoder().decode(plain);
  return JSON.parse(text) as T;
}

export async function decryptPhotoBlob(
  session: EnrolledSession,
  bundleKeyB64: string,
  stem: string,
): Promise<Blob> {
  const combined = await fetchEncryptedContent(session, `${stem}.enc`);
  const plain = await decryptAesGcm(combined, bundleKeyB64);
  return new Blob([plain as BlobPart], { type: "image/jpeg" });
}

const photoUrlCache = new Map<string, string>();

export function revokePhotoUrls(): void {
  for (const url of photoUrlCache.values()) URL.revokeObjectURL(url);
  photoUrlCache.clear();
}

export async function photoObjectUrl(
  session: EnrolledSession,
  bundleKeyB64: string,
  stem: string | null | undefined,
): Promise<string | null> {
  if (!stem) return null;
  const cached = photoUrlCache.get(stem);
  if (cached) return cached;
  try {
    const blob = await decryptPhotoBlob(session, bundleKeyB64, stem);
    const url = URL.createObjectURL(blob);
    photoUrlCache.set(stem, url);
    return url;
  } catch {
    return null;
  }
}
